import {defineNuxtPlugin} from '#app';
import {trackEvent} from '~/utils/productAnalytics';

/**
 * Maximum length for paths in PostHog tracking to prevent logging malicious/long inputs
 */
const MAX_TRACKING_PATH_LENGTH = 200;

/**
 * Truncates a path for safe logging in PostHog
 * @param {string} path - The path to truncate
 * @returns {string} Truncated path
 */
function truncatePathForTracking(path) {
  if (!path || typeof path !== 'string') return '';
  if (path.length <= MAX_TRACKING_PATH_LENGTH) return path;
  return path.substring(0, MAX_TRACKING_PATH_LENGTH) + '...';
}

/**
 * Validates that the current hostname is allowed
 * @returns {boolean} True if hostname is allowed
 */
function isAllowedHostname() {
  if (!process.client) return false;

  const hostname = window.location.hostname.toLowerCase();

  // Allow localhost for development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return true;
  }

  // Allow vector.co and its subdomains
  if (hostname === 'vector.co' || hostname.endsWith('.vector.co')) {
    return true;
  }

  // Allow Amplify preview branches
  if (hostname.includes('amplifyapp.com')) {
    return true;
  }

  // Allow .local domains for local development
  if (hostname.endsWith('.local')) {
    return true;
  }

  return false;
}

/**
 * Validates a path for safe navigation within the app.
 * Only allows relative paths (starting with '/') and rejects:
 * - External URLs (http://, https://, //)
 * - Protocol handlers (javascript:, data:, vbscript:, etc.)
 * - Paths that are too long
 * - Empty or invalid paths
 *
 * @param {string} path - The path to validate
 * @returns {string|null} The validated path, or null if invalid
 */
function validateNavigationPath(path) {
  // Must be a non-empty string
  if (typeof path !== 'string' || !path) {
    console.warn('[vectorRouter] Invalid path: must be a non-empty string');
    return null;
  }

  const trimmedPath = path.trim();

  // Reject empty paths after trimming
  if (!trimmedPath) {
    console.warn('[vectorRouter] Invalid path: empty after trimming');
    return null;
  }

  // Maximum path length to prevent DoS
  const MAX_PATH_LENGTH = 2048;
  if (trimmedPath.length > MAX_PATH_LENGTH) {
    console.warn('[vectorRouter] Invalid path: exceeds maximum length');
    return null;
  }

  // Only allow relative paths starting with '/'
  // Reject external URLs (http://, https://, //)
  if (!trimmedPath.startsWith('/')) {
    console.warn('[vectorRouter] Invalid path: must be a relative path starting with "/"');
    return null;
  }

  // Reject double slashes at start (//example.com)
  if (trimmedPath.startsWith('//')) {
    console.warn('[vectorRouter] Invalid path: cannot start with "//" (external URL)');
    return null;
  }

  // Reject protocol handlers (javascript:, data:, vbscript:, etc.)
  const protocolPattern = /^[a-z][a-z0-9+.-]*:/i;
  if (protocolPattern.test(trimmedPath)) {
    console.warn('[vectorRouter] Invalid path: contains protocol handler');
    return null;
  }

  // Reject paths with null bytes or other control characters
  // Check for control characters (0x00-0x1F and 0x7F) without using regex
  for (let i = 0; i < trimmedPath.length; i++) {
    const charCode = trimmedPath.charCodeAt(i);
    if ((charCode >= 0x00 && charCode <= 0x1f) || charCode === 0x7f) {
      console.warn('[vectorRouter] Invalid path: contains control characters');
      return null;
    }
  }

  return trimmedPath;
}

export default defineNuxtPlugin(() => {
  if (!process.client) {
    return;
  }

  // Validate hostname before exposing the router helper
  if (!isAllowedHostname()) {
    console.warn('[vectorRouter] Router helper not exposed: hostname not allowed');
    return;
  }

  const router = useRouter();

  // Expose navigation helper on window for external scripts (e.g., Chameleon)
  window.vectorRouter = {
    /**
     * Navigate to a new route using Vue Router
     * @param {string} path - The path to navigate to (e.g., '/segments/456' or '/?showInstallModal=true')
     * @returns {Promise<void>}
     */
    navigate: path => {
      const currentPath = router.currentRoute.value.path;
      const currentQuery = router.currentRoute.value.query;

      // Parse path and query string if present (e.g., '/?showInstallModal=true')
      let pathOnly = path;
      let queryParams = {};

      if (path.includes('?')) {
        const [basePath, queryString] = path.split('?', 2);
        pathOnly = basePath;

        // Parse query string into object
        const urlParams = new URLSearchParams(queryString);
        urlParams.forEach((value, key) => {
          queryParams[key] = value;
        });
      }

      const validatedPath = validateNavigationPath(pathOnly);

      if (!validatedPath) {
        trackEvent('router_helper_navigation_rejected', {
          reason: 'invalid_path',
          original_path: truncatePathForTracking(path),
          current_path: currentPath,
        });
        return Promise.reject(new Error('Invalid navigation path'));
      }

      // Prepare navigation target
      const navigationTarget =
        Object.keys(queryParams).length > 0
          ? {path: validatedPath, query: queryParams}
          : validatedPath;

      // Check if we're already on this route with same query params
      const samePath = currentPath === validatedPath;

      // Compare query params: target query params must all match current query params
      let sameQuery = false;
      if (Object.keys(queryParams).length === 0) {
        // Target has no query params - same if current also has none
        sameQuery = Object.keys(currentQuery).length === 0;
      } else {
        // Target has query params - check that all target params match current params
        const targetKeys = Object.keys(queryParams);
        sameQuery =
          targetKeys.length > 0 && targetKeys.every(key => currentQuery[key] === queryParams[key]);
      }

      if (samePath && sameQuery) {
        trackEvent('router_helper_navigation_skipped', {
          reason: 'already_on_route',
          path: validatedPath,
        });
        return Promise.resolve();
      }

      // Track navigation attempt
      trackEvent('router_helper_navigation_attempted', {
        from_path: currentPath,
        to_path: validatedPath,
        has_query_params: Object.keys(queryParams).length > 0,
        method: 'navigate',
      });

      return router
        .push(navigationTarget)
        .then(result => {
          // router.push() returns a NavigationFailure if navigation was prevented/aborted
          // or undefined/void if navigation succeeded
          if (result === false || (result && result.type)) {
            const newPath = router.currentRoute.value.path;

            trackEvent('router_helper_navigation_blocked', {
              from_path: currentPath,
              to_path: validatedPath,
              reason: result?.type || 'unknown',
              final_path: newPath,
            });

            return Promise.reject(new Error('Navigation was prevented or aborted'));
          }

          // Wait for next tick to ensure route has updated
          return nextTick().then(() => {
            const newPath = router.currentRoute.value.path;
            const newQuery = router.currentRoute.value.query;

            // Track successful navigation
            trackEvent('router_helper_navigation_success', {
              from_path: currentPath,
              to_path: validatedPath,
              final_path: newPath,
              route_name: router.currentRoute.value.name,
              has_query_params: Object.keys(newQuery).length > 0,
              method: 'navigate',
            });
          });
        })
        .catch(error => {
          console.error('[vectorRouter] Navigation failed:', error);

          // Track navigation failure
          trackEvent('router_helper_navigation_failed', {
            from_path: currentPath,
            to_path: validatedPath,
            error_name: error?.name,
            error_message: truncatePathForTracking(error?.message || ''),
            error_type: error?.type,
            final_path: router.currentRoute.value.path,
          });

          throw error;
        });
    },

    /**
     * Replace current route using Vue Router
     * @param {string} path - The path to replace with (e.g., '/segments/456' or '/?showInstallModal=true')
     * @returns {Promise<void>}
     */
    replace: path => {
      const currentPath = router.currentRoute.value.path;
      const currentQuery = router.currentRoute.value.query;

      // Parse path and query string if present (e.g., '/?showInstallModal=true')
      let pathOnly = path;
      let queryParams = {};

      if (path.includes('?')) {
        const [basePath, queryString] = path.split('?', 2);
        pathOnly = basePath;

        // Parse query string into object
        const urlParams = new URLSearchParams(queryString);
        urlParams.forEach((value, key) => {
          queryParams[key] = value;
        });
      }

      const validatedPath = validateNavigationPath(pathOnly);

      if (!validatedPath) {
        trackEvent('router_helper_navigation_rejected', {
          reason: 'invalid_path',
          original_path: truncatePathForTracking(path),
          current_path: currentPath,
          method: 'replace',
        });
        return Promise.reject(new Error('Invalid navigation path'));
      }

      // Prepare navigation target
      const navigationTarget =
        Object.keys(queryParams).length > 0
          ? {path: validatedPath, query: queryParams}
          : validatedPath;

      // Check if we're already on this route with same query params
      const samePath = currentPath === validatedPath;

      // Compare query params: target query params must all match current query params
      let sameQuery = false;
      if (Object.keys(queryParams).length === 0) {
        // Target has no query params - same if current also has none
        sameQuery = Object.keys(currentQuery).length === 0;
      } else {
        // Target has query params - check that all target params match current params
        const targetKeys = Object.keys(queryParams);
        sameQuery =
          targetKeys.length > 0 && targetKeys.every(key => currentQuery[key] === queryParams[key]);
      }

      if (samePath && sameQuery) {
        trackEvent('router_helper_navigation_skipped', {
          reason: 'already_on_route',
          path: validatedPath,
          method: 'replace',
        });
        return Promise.resolve();
      }

      // Track replacement attempt
      trackEvent('router_helper_navigation_attempted', {
        from_path: currentPath,
        to_path: validatedPath,
        has_query_params: Object.keys(queryParams).length > 0,
        method: 'replace',
      });

      return router
        .replace(navigationTarget)
        .then(result => {
          // Check if replacement was blocked
          if (result === false || (result && result.type)) {
            const newPath = router.currentRoute.value.path;

            trackEvent('router_helper_navigation_blocked', {
              from_path: currentPath,
              to_path: validatedPath,
              reason: result?.type || 'unknown',
              final_path: newPath,
              method: 'replace',
            });
            return Promise.reject(new Error('Route replacement was prevented or aborted'));
          }

          // Wait for next tick to ensure route has updated
          return nextTick().then(() => {
            const newPath = router.currentRoute.value.path;
            const newQuery = router.currentRoute.value.query;

            // Track successful replacement
            trackEvent('router_helper_navigation_success', {
              from_path: currentPath,
              to_path: validatedPath,
              final_path: newPath,
              route_name: router.currentRoute.value.name,
              has_query_params: Object.keys(newQuery).length > 0,
              method: 'replace',
            });
          });
        })
        .catch(error => {
          console.error('[vectorRouter] Route replacement failed:', error);

          // Track replacement failure
          trackEvent('router_helper_navigation_failed', {
            from_path: currentPath,
            to_path: validatedPath,
            error_name: error?.name,
            error_message: truncatePathForTracking(error?.message || ''),
            error_type: error?.type,
            method: 'replace',
          });

          throw error;
        });
    },

    /**
     * Get the current route path
     * @returns {string} Current route path
     */
    getCurrentPath: () => {
      return router.currentRoute.value.path;
    },
  };
});
